import type { IdZonaElevacaoPainel } from '../../dominio/didatico/elevacaoPainel'
import type { PerfilVeiculo } from '../../dominio/didatico/perfilVeiculo'
import type { IdPortaOficina } from './zonasOperacaoPorta'
import { pontosRebatimentoPainel } from './zonasRebatimentoPainel'
import { ANGULO_FECHADO, PIVO_BRACO, PONTA_BRACO, pontaDoBraco } from './geometriaAlargador'

export function pontosElevacaoPainel(perfil: PerfilVeiculo, idPorta: IdPortaOficina, altura = 0, dobra = 0) {
  const { coluna, yMedio, sinal, topoDobradicas, baseSuperiorA } = pontosRebatimentoPainel(perfil, idPorta)
  const yInferior = perfil.topoSoleiraY + 0.05
  const ySuperior = perfil.topoSoleiraY + 0.27
  const inferior = coluna(yInferior)
  const superior = coluna(ySuperior)
  const anguloDobra = Math.max(0, Math.min(1, dobra)) * Math.PI * 0.48
  const comprimentoAba = ySuperior - yInferior
  const dx = superior[0] - inferior[0]
  const pontaAba: [number, number, number] = [inferior[0] + dx * Math.cos(anguloDobra) + sinal * comprimentoAba * Math.sin(anguloDobra), inferior[1] - sinal * dx * Math.sin(anguloDobra) + comprimentoAba * Math.cos(anguloDobra), superior[2]]
  const apoioInferior: [number, number, number] = [superior[0], yInferior + 0.005, superior[2]]
  const apoioSuperior: [number, number, number] = [superior[0], ySuperior + 0.02 + altura, superior[2] + altura * 0.35]
  return { coluna, yMedio, sinal, topoDobradicas, baseSuperiorA, yInferior, ySuperior, inferior, superior, pontaAba, anguloDobra, apoioInferior, apoioSuperior }
}

export function zonaElevacaoPorId(perfil: PerfilVeiculo, idPorta: IdPortaOficina, id: IdZonaElevacaoPainel, altura = 0, dobra = 0) {
  const p = pontosElevacaoPainel(perfil, idPorta, altura, dobra)
  const posicoes: Record<IdZonaElevacaoPainel, [number, number, number]> = {
    corteAElevacao: p.coluna(p.yMedio),
    corteBaseASuperior: p.superior,
    corteBaseAInferior: p.inferior,
    abaElevacao: p.pontaAba,
    apoioAlargadorPainel: p.apoioInferior.map((n, i) => (n + p.apoioSuperior[i]) / 2) as [number, number, number],
  }
  return { id, posicao: posicoes[id], anguloGraus: id.startsWith('corte') ? 90 : 0 }
}

/** Pose do modelo existente: escala fixa e pontas superiores/inferiores nos dois apoios. */
export function poseAlargadorElevacao(apoioInferior: [number, number, number], apoioSuperior: [number, number, number], sinal: number) {
  const escala = 0.9
  const dy = apoioSuperior[1] - apoioInferior[1]
  const dz = apoioSuperior[2] - apoioInferior[2]
  const giroX = Math.atan2(dz, dy)
  const meiaAbertura = Math.hypot(dy, dz) / (2 * escala)
  // Ângulo do braço em que a ponta fica a `meiaAbertura` do eixo.
  const pivoY = PIVO_BRACO[1]
  const [pontaX, pontaY] = PONTA_BRACO
  const raio = Math.hypot(pontaX, pontaY)
  const theta = Math.asin(Math.max(-1, Math.min(1, (meiaAbertura - pivoY) / raio))) - Math.atan2(pontaY, pontaX)
  const anguloMandibula = theta - ANGULO_FECHADO
  const alcance = pontaDoBraco(anguloMandibula)[0]
  const posicao: [number, number, number] = [apoioInferior[0] + sinal * alcance * escala, (apoioInferior[1] + apoioSuperior[1]) / 2, (apoioInferior[2] + apoioSuperior[2]) / 2]
  return { escala, anguloMandibula, posicao, giroX, giroY: sinal > 0 ? Math.PI : 0 }
}
